/**
 * PageNavbarElement — F12.H T12.H.2.
 *
 * Pasek nawigacji strony — breadcrumb / linki kontekstowe na górze
 * formularza. LEAF element.
 *
 * Settings: { items: [{ label, url? }], showHome?: boolean }
 */

import { ChevronRight, Home } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

interface NavItem {
  label: string;
  url?: string;
}

export const PageNavbarElement: LayoutElementDefinition = {
  type: 'page-navbar',
  category: 'struktura',
  isContainer: false,
  // Wycofany z palety (decyzja właścicielki, T6) — atrapa breadcrumb bez wartości.
  // Zostaje zarejestrowany, więc istniejące zapisane layouty z `page-navbar` nadal
  // się renderują; nowych dodać się nie da. Wróci gdy powstanie wersja kontenerowa.
  hiddenFromPalette: true,
  label: 'Pasek nawigacji',
  icon: 'Navigation',
  defaultSettings: () => ({
    items: [{ label: 'Element 1' }, { label: 'Element 2' }] as NavItem[],
    showHome: true,
  }),
  render: ({ element, mode }) => {
    const settings = (element.settings ?? {}) as {
      items?: NavItem[];
      showHome?: boolean;
    };
    const items = settings.items ?? [];
    const isBuilder = mode === 'builder';

    return (
      <nav
        className={`flex items-center gap-1.5 py-2 text-sm ${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition rounded px-2' : ''}`}
        aria-label="breadcrumb"
        data-testid={`page-navbar-element-${element.id}`}
        data-element-type="page-navbar"
      >
        {settings.showHome !== false && (
          <Home className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" />
        )}
        {items.length === 0 && isBuilder && (
          <span className="text-xs italic text-text-muted">
            (brak elementów — dodaj w panelu właściwości)
          </span>
        )}
        {items.map((item, idx) => {
          // Bieżąca pozycja (ostatnia) = tekst podstawowy; wcześniejsze linki = tekst wtórny
          // z hoverem w kolorze akcentu (wg Figmy „08.4 Navigation / Breadcrumbs").
          const isCurrent = idx === items.length - 1;
          return (
            <span key={idx} className="flex items-center gap-1.5">
              {(idx > 0 || settings.showHome !== false) && (
                <ChevronRight className="h-3 w-3 text-text-tertiary" aria-hidden="true" />
              )}
              {item.url && !isCurrent ? (
                <a href={item.url} className="text-text-secondary hover:text-accent-600">
                  {item.label}
                </a>
              ) : (
                <span className={isCurrent ? 'text-text-primary' : 'text-text-secondary'}>
                  {item.label}
                </span>
              )}
            </span>
          );
        })}
      </nav>
    );
  },
};
